import { ToasterPWAUpdateReady } from '@proj-airi/stage-ui/components'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'
import { h, markRaw, onMounted, ref } from 'vue'
import { toast } from 'vue-sonner'

export const usePWAStore = defineStore('pwa', () => {
  const updateReadyHooks = ref<(() => void)[]>([])
  const breakpoints = useBreakpoints(breakpointsTailwind)
  const isMobile = breakpoints.smaller('md')
  const updateCheckInterval = ref<ReturnType<typeof setInterval> | null>(null)
  const serviceWorkerRegistration = ref<ServiceWorkerRegistration | null>(null)
  const isCheckingUpdate = ref(false)
  const hasUpdateAvailable = ref(false) // 🔧 新增：标记是否有更新
  const updateProgress = ref(0) // 🔧 新增：更新进度（0-100）
  const isDownloadingUpdate = ref(false) // 🔧 新增：是否正在下载更新
  let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null

  async function initializeServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (import.meta.env.SSR) {
      return null
    }
    if (import.meta.env.VITE_APP_TARGET_HUGGINGFACE_SPACE) {
      return null
    }

    // 首屏稳定后再注册 Service Worker，避免首次打开时立刻下载整套预缓存资源。
    await new Promise<void>((resolve) => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => resolve(), { timeout: 4000 })
      }
      else {
        globalThis.setTimeout(resolve, 2000)
      }
    })

    let pwaModule: { registerSW: typeof import('../modules/pwa')['registerSW'] } | null = null
    try {
      pwaModule = await import('../modules/pwa')
    }
    catch (err) {
      console.warn('⚠️ PWA 模块加载失败（可能是缓存或网络问题），PWA 功能将不可用:', err)
      return null
    }

    // 防御性检查：极端情况下 import 可能 resolve 为 null（浏览器模块缓存异常）
    if (!pwaModule) {
      console.warn('⚠️ PWA 模块加载异常（模块为空），PWA 功能将不可用')
      return null
    }

    const { registerSW } = pwaModule
    let settleRegistration: (registration: ServiceWorkerRegistration | null) => void = () => {}
    let registrationSettled = false
    const registered = new Promise<ServiceWorkerRegistration | null>((resolve) => {
      settleRegistration = (registration) => {
        if (registrationSettled)
          return
        registrationSettled = true
        resolve(registration)
      }
    })

    const updateSW = registerSW({
      // 立即检查更新
      immediate: true,
      onNeedRefresh: () => {
        // 🔧 标记有更新可用
        hasUpdateAvailable.value = true

        const id = nanoid()
        toast(markRaw(h(ToasterPWAUpdateReady, { id, onUpdate: () => updateSW() })), {
          id,
          duration: 30000,
          position: isMobile.value ? 'top-center' : 'bottom-right',
        })
      },
      onRegistered: (registration) => {
        // ⭐ 定期检查更新（每 15 分钟检查一次，更及时）
        if (registration) {
          // 保存 registration 引用，供手动检查使用
          serviceWorkerRegistration.value = registration

          console.info('✅ Service Worker 已注册，启动定期更新检查')

          // 应用启动时立即检查一次更新
          console.info('🔄 应用启动，立即检查 PWA 更新...')
          registration.update().catch((err) => {
            console.warn('⚠️ 初始更新检查失败:', err)
          })

          // 定期检查更新（每 30 分钟一次，减少失败频率）
          updateCheckInterval.value = setInterval(() => {
            console.info('🔄 定期检查 PWA 更新...')
            registration.update().catch((err) => {
              console.warn('⚠️ 定期更新检查失败:', err)
            })
          }, 30 * 60 * 1000) // 🔧 改为每 30 分钟检查一次
        }
        settleRegistration(registration ?? null)
      },
      onOfflineReady: () => {
        console.info('✅ PWA 已准备好离线使用')
      },
    })

    updateReadyHooks.value.push(updateSW)
    // 某些浏览器注册失败时不会回调 onRegistered，避免手动检查永久等待。
    globalThis.setTimeout(() => settleRegistration(serviceWorkerRegistration.value), 5000)
    return registered
  }

  onMounted(() => {
    registrationPromise = initializeServiceWorker().catch((err) => {
      console.warn('⚠️ Service Worker 初始化失败:', err)
      return null
    })
  })

  /**
   * 手动检查更新（带重试机制）
   */
  async function checkForUpdate() {
    if (isCheckingUpdate.value) {
      console.info('⏳ 正在检查更新中，请稍候...')
      return false
    }

    const MAX_RETRIES = 3
    const RETRY_DELAY = 2000 // 2秒

    try {
      isCheckingUpdate.value = true
      console.info('🔄 手动检查 PWA 更新...')
      toast.info('正在检查更新...')

      // 尝试获取最新的 Service Worker Registration
      let registration = serviceWorkerRegistration.value

      // 🔧 修复：更全面的判断条件
      if (!registration || (!registration.active && !registration.waiting && !registration.installing)) {
        // 首次启动可能仍在等待空闲时机注册，主动检查应等待这次初始化完成。
        if (registrationPromise)
          registration = await registrationPromise

        console.info('🔄 重新获取 Service Worker Registration...')
        const newRegistration = registration || await navigator.serviceWorker.getRegistration()

        if (newRegistration) {
          registration = newRegistration
          serviceWorkerRegistration.value = newRegistration
        }
        else {
          console.warn('⚠️ Service Worker 未注册，无法检查更新')
          toast.error('PWA 未初始化，无法检查更新')
          return false
        }
      }

      // 检查 registration 对象是否可用
      if (!registration || typeof registration.update !== 'function') {
        console.warn('⚠️ Service Worker Registration 不可用')
        toast.error('Service Worker 状态异常，请刷新页面后重试')
        return false
      }

      // 🔧 重置更新标志
      hasUpdateAvailable.value = false

      // 🔧 带重试的更新检查（增加超时到 30 秒）
      let lastError: Error | null = null
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          console.info(`🔄 尝试检查更新 (${attempt}/${MAX_RETRIES})`)

          const updatePromise = registration.update()
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('更新检查超时')), 30000), // 🔧 增加到 30 秒
          )

          await Promise.race([updatePromise, timeoutPromise])
          lastError = null
          break // 成功，跳出重试循环
        }
        catch (err) {
          lastError = err as Error
          console.warn(`⚠️ 第 ${attempt} 次更新检查失败:`, err)

          if (attempt < MAX_RETRIES) {
            console.info(`⏳ ${RETRY_DELAY / 1000} 秒后重试...`)
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
          }
        }
      }

      // 如果所有重试都失败，抛出最后一个错误
      if (lastError) {
        throw lastError
      }

      // 🔧 等待 Service Worker 检查完成并触发 onNeedRefresh（如果有更新）
      // 使用轮询检查而不是固定等待时间，更可靠
      const maxWaitTime = 10000 // 最多等待 10 秒
      const checkInterval = 500 // 每 500ms 检查一次
      const startTime = Date.now()

      while (Date.now() - startTime < maxWaitTime) {
        // 如果已经检测到更新，立即退出等待
        if (hasUpdateAvailable.value) {
          console.info('🔄 发现新版本，等待用户确认更新')
          return true
        }

        // 检查是否有 waiting 或 installing 状态的 Service Worker
        if (registration.waiting || registration.installing) {
          console.info('🔄 检测到新的 Service Worker，等待激活...')
          await new Promise(resolve => setTimeout(resolve, checkInterval))
          continue
        }

        await new Promise(resolve => setTimeout(resolve, checkInterval))
      }

      // 等待超时后，如果仍未检测到更新，则显示"已是最新版本"
      if (!hasUpdateAvailable.value) {
        toast.success('您已使用最新版本！')
        console.info('✅ 已是最新版本')
      }

      return true
    }
    catch (error) {
      console.error('❌ 检查更新失败（所有重试均失败）:', error)

      // 🔧 提供更详细和友好的错误信息
      if (error instanceof DOMException) {
        console.error('DOMException 详情:', {
          name: error.name,
          message: error.message,
          code: error.code,
        })
        toast.error('Service Worker 已失效，请刷新页面后重试', {
          duration: 5000,
        })
      }
      else if (String(error).includes('超时')) {
        toast.error('网络连接较慢，更新检查超时。请稍后重试或检查网络连接', {
          duration: 5000,
        })
      }
      else if (String(error).includes('Failed to fetch')) {
        toast.error('无法连接到服务器，请检查网络连接后重试', {
          duration: 5000,
        })
      }
      else {
        toast.error(`检查更新失败: ${String(error).substring(0, 50)}`, {
          duration: 5000,
        })
      }

      return false
    }
    finally {
      isCheckingUpdate.value = false
    }
  }

  return {
    updateReadyHooks,
    updateCheckInterval,
    serviceWorkerRegistration,
    isCheckingUpdate,
    hasUpdateAvailable,
    updateProgress,
    isDownloadingUpdate,
    checkForUpdate,
  }
})
